/**
 * NeighborLetterModal — make a neighbor letter for one agency review and put it on the client's Hub
 * (docs/NEIGHBOR_LETTERS.md, Matt 2026-11-03). Opens via CustomEvent('open-neighbor-letter',
 * { detail: { reviewId, reviewName } }) from the agency review's ⋯ menu.
 *
 * Two letters: the Neighbor Input Form for new windows or doors, and a Letter of Support for a
 * project going before a design review board. Prefilled from the review; every line stays editable.
 * The letter's words live server-side in api/_neighborLetters.js, one place. The client asks the
 * neighbors and uploads each signed letter on the Hub card; we never send anything to a neighbor.
 */
import { useEffect, useState } from 'react'
import { ctx } from '../outline/ctx.js'
import { confirmDialog } from './Dialog.jsx'
import { getNeighborLetterPrefill, renderNeighborLetter, postNeighborLetter } from '../services/planCheckLetter.js'

const EMPTY = {
  kind: 'window', agency: '', required: false, heading: 'Proposed Window and Door Additions',
  board: '', hearingDate: '', address: '', projectName: '', scope: '', note: '',
}

const KINDS = [
  { key: 'window', name: 'Window and door input form', line: 'A neighbor reviews new windows or doors the planner asked about.' },
  { key: 'support', name: 'Letter of support', line: 'A neighbor supports a project going before a design review board.' },
]

export default function NeighborLetterModal() {
  const [open, setOpen] = useState(false)
  const [reviewId, setReviewId] = useState(null)
  const [reviewName, setReviewName] = useState('')
  const [L, setL] = useState(EMPTY)
  const [known, setKnown] = useState(null)   // prefill: { hub, boards, existing, projectName }
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState('')
  const set = (k, v) => setL(s => ({ ...s, [k]: v }))

  useEffect(() => {
    function onOpen(e) {
      const d = (e && e.detail) || {}
      if (!d.reviewId) return
      setReviewId(d.reviewId); setReviewName(d.reviewName || 'Agency review')
      setL(EMPTY); setKnown(null); setError(''); setDone(''); setBusy(false); setLoading(true); setOpen(true)
      getNeighborLetterPrefill(d.reviewId)
        .then(p => {
          setKnown(p)
          setL(s => ({ ...s, agency: p.agency || '', required: !!p.required, address: p.address || '', projectName: p.projectName || '', board: (p.boards && p.boards[0]) || '' }))
        })
        .catch(err => setError(err.message))
        .finally(() => setLoading(false))
    }
    window.addEventListener('open-neighbor-letter', onOpen)
    return () => window.removeEventListener('open-neighbor-letter', onOpen)
  }, [])

  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const existing = known && (known.existing || []).find(x => x.kind === L.kind && x.published)

  async function preview() {
    setBusy(true); setError('')
    try { window.open(await renderNeighborLetter(L), '_blank', 'noopener') } catch (err) { setError(err.message) }
    setBusy(false)
  }

  async function post() {
    const proj = (known && known.projectName) || 'the project'
    const ok = await confirmDialog({
      title: existing ? 'Replace the letter on the Client Hub?' : 'Put it on the Client Hub?',
      message: existing
        ? `The card on ${proj}'s Client Hub gets this version of the letter. Letters already returned stay on the card.`
        : `A card with this letter goes on ${proj}'s Client Hub, and the people who get its updates are emailed. They print or send it to their neighbors and upload each signed letter on the card.`,
      confirmLabel: existing ? 'Replace the letter' : 'Put it on the Client Hub',
    })
    if (!ok) return
    setBusy(true); setError('')
    try {
      const m = (ctx.currentMember && ctx.currentMember.fields) || {}
      const r = await postNeighborLetter(reviewId, L, m['Member Name'] || m['Name'] || '')
      setDone(r.created ? `On ${proj}'s Client Hub. Returned letters will show under this review's Stakeholder Input.` : `The letter on ${proj}'s Client Hub is replaced.`)
      setKnown(k => k ? { ...k, existing: [...(k.existing || []).filter(x => x.kind !== L.kind), { id: r.entryId, kind: L.kind, published: true }] } : k)
    } catch (err) { setError(err.message) }
    setBusy(false)
  }

  if (!open) return null
  const S = STYLES
  const field = (k, label, opts = {}) => (
    <label style={{ ...S.field, ...(opts.wide ? { gridColumn: '1 / -1' } : null) }}>
      <span style={S.label}>{label}</span>
      {opts.multiline
        ? <textarea style={{ ...S.input, minHeight: 56, resize: 'vertical' }} value={L[k]} placeholder={opts.placeholder || ''} onChange={e => set(k, e.target.value)} />
        : <input style={S.input} value={L[k]} placeholder={opts.placeholder || ''} list={opts.list} onChange={e => set(k, e.target.value)} />}
    </label>
  )
  const noHub = known && !known.hub
  return (
    <div style={S.backdrop} onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false) }}>
      <div data-modal-id="NeighborLetterModal" style={S.box}>
        <div style={S.title}>Neighbor letter</div>
        <div style={S.sub}>{reviewName}{loading ? ' · reading the review…' : ''}</div>

        <div style={S.kinds}>
          {KINDS.map(k => (
            <button key={k.key} style={S.kind(L.kind === k.key)} onClick={() => set('kind', k.key)}>
              <span style={{ fontWeight: 600 }}>{k.name}</span>
              <span style={{ color: 'var(--aj-text-muted)', fontSize: 11 }}>{k.line}</span>
            </button>
          ))}
        </div>

        <div style={S.group}>The project</div>
        <div style={S.grid}>
          {field('address', 'Project address', { wide: true })}
          {field('scope', L.kind === 'support' ? 'What is proposed (one line)' : 'What the neighbor is reviewing (one line)', {
            wide: true, placeholder: L.kind === 'support' ? 'A second-story addition and remodel' : 'New corner windows in the primary bedroom' })}
        </div>

        <div style={S.group}>{L.kind === 'support' ? 'The hearing' : 'The agency'}</div>
        <div style={S.grid}>
          {field('agency', 'Agency')}
          {L.kind === 'support'
            ? <>
                {field('board', 'Board', { list: 'aj-nl-boards', placeholder: 'Design Review Board' })}
                {field('hearingDate', 'Hearing date (optional)', { placeholder: 'December 11, 2026' })}
              </>
            : field('heading', 'Heading')}
        </div>
        {known && known.boards && known.boards.length > 0 && <datalist id="aj-nl-boards">{known.boards.map(b => <option key={b} value={b} />)}</datalist>}
        {L.kind === 'window' && (
          <label style={S.check}>
            <input type="checkbox" checked={L.required} onChange={e => set('required', e.target.checked)} />
            <span>The agency requires neighbor notice (the letter says "requires"; unticked it says the agency "asks")</span>
          </label>
        )}

        <div style={S.group}>On the Client Hub</div>
        <div style={S.grid}>
          {field('note', 'A note to the client (optional): which neighbors to ask', { wide: true, multiline: true, placeholder: 'The neighbors at 2050 and 2070 Temple Hills, who look toward the new windows.' })}
        </div>
        <div style={S.hint}>
          The card tells the client to share the plans with each neighbor, have them sign, and upload each signed letter. Every
          letter that comes back is filed under this review's Stakeholder Input, and the project channel hears about it.
        </div>

        {noHub && <div style={S.warn}>This project has no Client Hub yet. You can still make the PDF and send it to the client yourself.</div>}
        {error && <div style={S.error}>{error}</div>}
        {done && <div style={S.done}>{done}</div>}
        <div style={S.actions}>
          <button style={S.btn} onClick={() => setOpen(false)}>{done ? 'Close' : 'Cancel'}</button>
          <button style={S.btn} disabled={busy || loading} onClick={preview}>Preview PDF</button>
          <button style={{ ...S.primary, ...(noHub || busy || loading ? { opacity: 0.45, cursor: 'default' } : null) }} disabled={noHub || busy || loading} onClick={post}>
            {busy ? 'Working…' : existing ? 'Replace on the Client Hub' : 'Put on the Client Hub'}
          </button>
        </div>
      </div>
    </div>
  )
}

const ff = '"DM Sans",sans-serif'
const STYLES = {
  backdrop: { position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  box: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 580, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', fontFamily: ff, color: 'var(--aj-text)' },
  title: { fontSize: 13, fontWeight: 600 },
  sub: { fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 },
  kinds: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 14 },
  kind: on => ({ display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left', padding: '10px 12px', borderRadius: 6, cursor: 'pointer', fontFamily: ff, fontSize: 12, color: 'var(--aj-text)',
    border: on ? '1px solid var(--aj-accent)' : '0.5px solid var(--aj-border-mid)', background: on ? 'var(--aj-bg-subtle)' : 'var(--aj-bg-surface)' }),
  group: { fontSize: 11, fontWeight: 600, color: 'var(--aj-text-mid)', margin: '14px 0 6px' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  field: { display: 'flex', flexDirection: 'column', gap: 3 },
  label: { fontSize: 11, color: 'var(--aj-text-muted)' },
  input: { fontFamily: ff, fontSize: 12, padding: '6px 8px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', boxSizing: 'border-box', width: '100%' },
  check: { display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12, marginTop: 8, color: 'var(--aj-text-mid)' },
  hint: { fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 6, lineHeight: 1.5 },
  warn: { fontSize: 12, color: 'var(--aj-text-mid)', marginTop: 12, padding: '8px 10px', borderRadius: 4, background: 'var(--aj-bg-subtle)' },
  error: { color: '#c0392b', fontSize: 12, marginTop: 10 },
  done: { color: 'var(--aj-text)', fontSize: 12, marginTop: 10 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
  btn: { fontFamily: ff, fontSize: 12, padding: '6px 14px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', cursor: 'pointer' },
  primary: { fontFamily: ff, fontSize: 12, padding: '6px 14px', borderRadius: 4, border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer' },
}
